import { baseKeymap } from 'prosemirror-commands'
import { history, redo, undo } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import { Schema, type Node, type Fragment } from 'prosemirror-model'
import { EditorState } from 'prosemirror-state'
import type { CellSegment } from '../schemas'

/** One text block, with literal newlines and atomic reference occurrences. */
export const cellEditorSchema = new Schema({
  nodes: {
    doc: { content: 'paragraph' },
    paragraph: { content: 'inline*', whitespace: 'pre', toDOM: () => ['div', 0] },
    text: { group: 'inline' },
    verification: {
      inline: true,
      group: 'inline',
      atom: true,
      toDOM: () => ['span', { contenteditable: 'false' }, ''],
    },
    reference: {
      inline: true,
      group: 'inline',
      atom: true,
      draggable: true,
      attrs: { id: {} },
      toDOM: () => ['span', { contenteditable: 'false' }, ''],
    },
  },
})

export function createCellEditorState(segments: CellSegment[], needsVerification = false): EditorState {
  const nodes: Node[] = []
  for (const segment of segments) {
    if (segment.text) nodes.push(cellEditorSchema.text(segment.text))
    for (const id of segment.reference_ids) nodes.push(cellEditorSchema.nodes.reference.create({ id }))
  }
  if (needsVerification) nodes.push(cellEditorSchema.nodes.verification.create())
  return EditorState.create({
    doc: cellEditorSchema.node('doc', null, cellEditorSchema.node('paragraph', null, nodes)),
    plugins: [history(), keymap({ 'Mod-z': undo, 'Mod-Shift-z': redo, 'Mod-y': redo }), keymap(baseKeymap)],
  })
}

export function cellEditorContent(doc: Node): CellSegment[] {
  return cellEditorFragmentContent(doc.content)
}

export function cellEditorFragmentContent(fragment: Fragment): CellSegment[] {
  const segments: CellSegment[] = []
  let segment: CellSegment = { text: '', reference_ids: [] }
  fragment.descendants((node) => {
    if (node.isText) {
      if (segment.reference_ids.length) {
        segments.push(segment)
        segment = { text: '', reference_ids: [] }
      }
      segment.text += node.text
    } else if (node.type.name === 'reference') {
      const id = node.attrs.id as string
      // Deleting the text between two occurrences must not merge away either chip.
      if (segment.reference_ids.includes(id)) {
        segments.push(segment)
        segment = { text: '', reference_ids: [] }
      }
      segment.reference_ids.push(id)
    }
  })
  if (segment.text || segment.reference_ids.length) segments.push(segment)
  return segments
}

export function cellEditorNeedsVerification(doc: Node): boolean {
  let found = false
  doc.descendants((node) => {
    if (node.type.name === 'verification') found = true
  })
  return found
}
